import Dexie, { type EntityTable } from "dexie";
import {
  canonicalChangeSchema,
  canvasItemSchema,
  isLockedItem,
  mergeClipOperation,
  mergeItemOperation,
  recoveredVersionSchema,
  spaceSchema,
  syncV2SnapshotSchema,
  withoutReadableBody,
  type CanonicalChange,
  type CanvasItem,
  type RecoveredVersion,
  type Snapshot,
  type Space,
  type SyncChange,
  type SyncOperation,
  type SyncOperationResult,
  type SyncV2Snapshot
} from "@bedrock/domain";
import { createMissingEntityRepair, createSnapshotRestoreOperation, searchLocalContent, type CachedAsset, type LocalStore, type SearchResult, type SyncIssue } from "@bedrock/local-store";

interface MetaRow { key: string; value: number | string }
interface OutboxRow extends SyncOperation { queuedAt: number }

class BedrockDatabase extends Dexie {
  spaces!: EntityTable<Space, "id">;
  items!: EntityTable<CanvasItem, "id">;
  outbox!: EntityTable<OutboxRow, "opId">;
  recovered!: EntityTable<RecoveredVersion, "id">;
  assets!: EntityTable<CachedAsset, "assetId">;
  meta!: EntityTable<MetaRow, "key">;
  syncIssues!: EntityTable<SyncIssue, "id">;

  constructor(name: string) {
    super(name);
    this.version(1).stores({
      spaces: "id, updatedAt, deletedAt, title",
      items: "id, spaceId, type, updatedAt, deletedAt, parentStackId",
      outbox: "opId, queuedAt, entityId",
      recovered: "id, itemId, recoveredAt",
      assets: "assetId, lastAccessedAt, pinned",
      meta: "key"
    });
    this.version(2).stores({
      syncIssues: "id, createdAt, operation.opId"
    });
  }
}

export class WebLocalStore implements LocalStore {
  readonly #db: BedrockDatabase;
  readonly #listeners = new Set<() => void>();
  readonly #channel: BroadcastChannel | undefined;

  constructor(name = "bedrock-local") {
    this.#db = new BedrockDatabase(name);
    this.#channel = typeof BroadcastChannel === "undefined" ? undefined : new BroadcastChannel(`${name}-changes`);
    this.#channel?.addEventListener("message", () => this.#emit(false));
  }

  async initialize(): Promise<void> {
    await this.#db.open();
    if (!(await this.#db.meta.get("cursor"))) await this.#db.meta.put({ key: "cursor", value: 0 });
  }

  async close(): Promise<void> {
    this.#channel?.close();
    this.#db.close();
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  async listSpaces(options?: { includeDeleted?: boolean }): Promise<Space[]> {
    const spaces = (await this.#db.spaces.orderBy("updatedAt").reverse().toArray()).map((space) => spaceSchema.parse(space));
    return options?.includeDeleted ? spaces : spaces.filter((space) => space.deletedAt === null);
  }

  async listItems(spaceId: string, options?: { includeDeleted?: boolean }): Promise<CanvasItem[]> {
    const items = await this.#db.items.where("spaceId").equals(spaceId).sortBy("zIndex");
    return options?.includeDeleted ? items : items.filter((item) => item.deletedAt === null);
  }

  getItem(itemId: string): Promise<CanvasItem | undefined> {
    return this.#db.items.get(itemId);
  }

  async saveSpace(space: Space, operation: SyncOperation): Promise<void> {
    const parsed = spaceSchema.parse(space);
    await this.#db.transaction("rw", this.#db.spaces, this.#db.outbox, async () => {
      await this.#db.spaces.put(parsed);
      await this.#db.outbox.put({ ...operation, queuedAt: Date.now() });
    });
    this.#emit();
  }

  async saveItem(item: CanvasItem, operation: SyncOperation): Promise<void> {
    canvasItemSchema.parse(item);
    await this.#db.transaction("rw", this.#db.items, this.#db.outbox, this.#db.recovered, async () => {
      await this.#db.items.put(item);
      if (isLockedItem(item)) {
        // Unsent edits and recovered copies would otherwise keep the readable text on this device.
        for (const pending of await this.#db.outbox.where("entityId").equals(item.id).toArray()) {
          await this.#db.outbox.put({ ...withoutReadableBody(pending, item.props), queuedAt: pending.queuedAt });
        }
        await this.#db.recovered.where("itemId").equals(item.id).delete();
      }
      await this.#db.outbox.put({ ...operation, queuedAt: Date.now() });
    });
    this.#emit();
  }

  async permanentlyDelete(entityType: "space" | "item", entityId: string, operation: SyncOperation): Promise<void> {
    await this.#db.transaction("rw", [this.#db.spaces, this.#db.items, this.#db.outbox, this.#db.recovered, this.#db.assets], async () => {
      const removedItems = entityType === "space"
        ? await this.#db.items.where("spaceId").equals(entityId).toArray()
        : [await this.#db.items.get(entityId)].filter((item): item is CanvasItem => item !== undefined);
      const removedItemIds = new Set(removedItems.map((item) => item.id));
      const candidateAssets = new Set(removedItems.flatMap(itemAssetIds));
      const remainingItems = (await this.#db.items.toArray()).filter((item) => !removedItemIds.has(item.id));
      const referencedAssets = new Set(remainingItems.flatMap(itemAssetIds));

      for (const assetId of candidateAssets) if (!referencedAssets.has(assetId)) await this.#db.assets.delete(assetId);
      for (const itemId of removedItemIds) await this.#db.recovered.where("itemId").equals(itemId).delete();
      await this.#db.items.bulkDelete([...removedItemIds]);
      if (entityType === "space") await this.#db.spaces.delete(entityId);

      const removedEntityKeys = new Set([
        `${entityType}:${entityId}`,
        ...[...removedItemIds].map((itemId) => `item:${itemId}`)
      ]);
      const superseded = (await this.#db.outbox.toArray())
        .filter((pending) => removedEntityKeys.has(`${pending.entityType}:${pending.entityId}`))
        .map((pending) => pending.opId);
      await this.#db.outbox.bulkDelete(superseded);
      await this.#db.outbox.put({ ...operation, queuedAt: Date.now() });
    });
    this.#emit();
  }

  async saveRecoveredVersion(version: RecoveredVersion): Promise<void> {
    await this.#db.recovered.put(recoveredVersionSchema.parse(version));
    this.#emit();
  }

  async listRecoveredVersions(itemId: string): Promise<RecoveredVersion[]> {
    return (await this.#db.recovered.where("itemId").equals(itemId).reverse().sortBy("recoveredAt")).map((version) => recoveredVersionSchema.parse(version));
  }

  async importSnapshot(snapshot: Snapshot): Promise<void> {
    await this.#db.transaction("rw", this.#db.spaces, this.#db.items, this.#db.meta, async () => {
      await this.#db.spaces.clear();
      await this.#db.items.clear();
      await this.#db.spaces.bulkPut(snapshot.spaces);
      await this.#db.items.bulkPut(snapshot.items);
      await this.#db.meta.put({ key: "cursor", value: snapshot.latestSequence });
    });
    this.#emit();
  }

  async applyPullChanges(changes: SyncChange[]): Promise<void> {
    await this.#db.transaction("rw", this.#db.spaces, this.#db.items, async () => {
      for (const change of changes) {
        const operation = change.operation;
        if (operation.entityType === "item") {
          const current = await this.#db.items.get(operation.entityId);
          if (current) await this.#db.items.put(mergeItemOperation(current, operation).item);
          else if ("entity" in operation.patch) {
            const parsed = canvasItemSchema.safeParse(operation.patch["entity"]);
            if (parsed.success) await this.#db.items.put(parsed.data);
          }
        } else if (operation.entityType === "space" && "entity" in operation.patch) {
          const parsed = spaceSchema.safeParse(operation.patch["entity"]);
          if (parsed.success) await this.#db.spaces.put(parsed.data);
        } else if (operation.entityType === "clip") {
          const itemId = operation.patch["itemId"];
          if (typeof itemId !== "string") continue;
          const current = await this.#db.items.get(itemId);
          if (!current || current.props.kind !== "web-clip") continue;
          await this.#db.items.put(mergeClipOperation(current, operation));
        }
      }
    });
    if (changes.length > 0) this.#emit();
  }

  async getOutbox(limit = 500): Promise<SyncOperation[]> {
    const rows = await this.#db.outbox.orderBy("queuedAt").limit(limit).toArray();
    return rows.map((row) => ({ opId: row.opId, deviceId: row.deviceId, entityType: row.entityType, entityId: row.entityId, baseRevision: row.baseRevision, clock: row.clock, patch: row.patch }));
  }

  getPendingOperationCount(): Promise<number> {
    return this.#db.outbox.count();
  }

  async acknowledgeOperations(operationIds: string[]): Promise<void> {
    await this.#db.outbox.bulkDelete(operationIds);
  }

  async getCursor(): Promise<number> {
    const value = (await this.#db.meta.get("cursor"))?.value;
    return typeof value === "number" ? value : 0;
  }

  async setCursor(sequence: number): Promise<void> {
    await this.#db.meta.put({ key: "cursor", value: sequence });
  }

  async getSyncCursor(): Promise<number | null> {
    const value = (await this.#db.meta.get("sync-v2-cursor"))?.value;
    return typeof value === "number" ? value : null;
  }

  async applyCanonicalPage(changes: CanonicalChange[], nextCursor: number): Promise<void> {
    const parsed = changes.map((change) => canonicalChangeSchema.parse(change));
    await this.#db.transaction("rw", [this.#db.spaces, this.#db.items, this.#db.outbox, this.#db.recovered, this.#db.meta], async () => {
      const pendingEntities = new Set((await this.#db.outbox.toArray()).flatMap((operation) =>
        operation.entityType === "space" || operation.entityType === "item"
          ? [`${operation.entityType}:${operation.entityId}`]
          : []
      ));
      for (const change of parsed) {
        const pendingKey = change.kind === "space" || change.kind === "item"
          ? `${change.kind}:${change.action === "remove" ? change.entityId : change.entity.id}`
          : undefined;
        // Its pending operation will emit a later canonical version, so keep the local row while advancing the cursor.
        if (pendingKey && pendingEntities.has(pendingKey)) continue;
        if (change.action === "remove") {
          if (change.kind === "space") await this.#db.spaces.delete(change.entityId);
          if (change.kind === "item") await this.#db.items.delete(change.entityId);
          if (change.kind === "recovered-version") await this.#db.recovered.delete(change.entityId);
          continue;
        }
        if (change.kind === "space") await this.#db.spaces.put(change.entity);
        if (change.kind === "item") await this.#db.items.put(change.entity);
        if (change.kind === "recovered-version") await this.#db.recovered.put(change.entity);
      }
      await this.#db.meta.put({ key: "sync-v2-cursor", value: nextCursor });
    });
    if (parsed.length > 0) this.#emit();
  }

  async getCanonicalSnapshot(): Promise<SyncV2Snapshot> {
    const [spaces, items, recoveredVersions, latestCursor] = await Promise.all([
      this.#db.spaces.toArray(),
      this.#db.items.toArray(),
      this.#db.recovered.toArray(),
      this.getSyncCursor()
    ]);
    return syncV2SnapshotSchema.parse({ spaces, items, recoveredVersions, latestCursor: latestCursor ?? 0 });
  }

  async stageMissingSnapshotEntities(snapshot: SyncV2Snapshot, deviceId: string): Promise<number> {
    const parsed = syncV2SnapshotSchema.parse(snapshot);
    const cloudSpaces = new Set(parsed.spaces.map((space) => space.id));
    const cloudItems = new Set(parsed.items.map((item) => item.id));
    let staged = 0;
    await this.#db.transaction("rw", this.#db.spaces, this.#db.items, this.#db.outbox, async () => {
      const queued = new Set((await this.#db.outbox.toArray()).map((operation) => `${operation.entityType}:${operation.entityId}`));
      for (const space of await this.#db.spaces.orderBy("updatedAt").toArray()) {
        const key = `space:${space.id}`;
        if (cloudSpaces.has(space.id) || queued.has(key)) continue;
        await this.#db.outbox.put({ ...createSnapshotRestoreOperation("space", space, deviceId), queuedAt: Date.now() });
        queued.add(key);
        staged += 1;
      }
      for (const item of await this.#db.items.orderBy("updatedAt").toArray()) {
        const key = `item:${item.id}`;
        if (cloudItems.has(item.id) || queued.has(key)) continue;
        await this.#db.outbox.put({ ...createSnapshotRestoreOperation("item", item, deviceId), queuedAt: Date.now() });
        queued.add(key);
        staged += 1;
      }
    });
    if (staged > 0) this.#emit();
    return staged;
  }

  async replaceCanonicalSnapshotIfNoPending(snapshot: SyncV2Snapshot): Promise<boolean> {
    const parsed = syncV2SnapshotSchema.parse(snapshot);
    let replaced = false;
    await this.#db.transaction("rw", [this.#db.spaces, this.#db.items, this.#db.recovered, this.#db.outbox, this.#db.syncIssues, this.#db.meta], async () => {
      if (await this.#db.outbox.count() > 0 || await this.#db.syncIssues.count() > 0) return;
      await this.#db.spaces.clear();
      await this.#db.items.clear();
      await this.#db.recovered.clear();
      await this.#db.spaces.bulkPut(parsed.spaces);
      await this.#db.items.bulkPut(parsed.items);
      await this.#db.recovered.bulkPut(parsed.recoveredVersions);
      await this.#db.meta.put({ key: "sync-v2-cursor", value: parsed.latestCursor });
      replaced = true;
    });
    if (replaced) this.#emit();
    return replaced;
  }

  async recordSyncSuccess(timestamp: number): Promise<void> {
    await this.#db.meta.put({ key: "last-sync-success", value: timestamp });
  }

  async getLastSyncSuccess(): Promise<number | null> {
    const value = (await this.#db.meta.get("last-sync-success"))?.value;
    return typeof value === "number" ? value : null;
  }

  async listSyncIssues(): Promise<SyncIssue[]> {
    return this.#db.syncIssues.orderBy("createdAt").toArray();
  }

  async recoverSyncIssues(): Promise<number> {
    let repaired = 0;
    await this.#db.transaction("rw", this.#db.outbox, this.#db.syncIssues, async () => {
      for (const issue of await this.#db.syncIssues.orderBy("createdAt").toArray()) {
        const operation = createMissingEntityRepair(issue);
        if (!operation) continue;
        await this.#db.outbox.put({ ...operation, queuedAt: Date.now() });
        await this.#db.syncIssues.delete(issue.id);
        repaired += 1;
      }
    });
    if (repaired > 0) this.#emit();
    return repaired;
  }

  async quarantineRejectedOperation(operation: SyncOperation, result: SyncOperationResult): Promise<void> {
    let entitySnapshot: Space | CanvasItem | null = null;
    await this.#db.transaction("rw", [this.#db.outbox, this.#db.syncIssues, this.#db.spaces, this.#db.items, this.#db.recovered], async () => {
      if (result.errorCode === "permanently_deleted" && (operation.entityType === "space" || operation.entityType === "item")) {
        const itemIds = operation.entityType === "space"
          ? await this.#db.items.where("spaceId").equals(operation.entityId).primaryKeys()
          : [operation.entityId];
        for (const itemId of itemIds) await this.#db.recovered.where("itemId").equals(itemId).delete();
        await this.#db.items.bulkDelete(itemIds);
        if (operation.entityType === "space") await this.#db.spaces.delete(operation.entityId);
        await this.#db.syncIssues.delete(`issue_${operation.opId}`);
        await this.#db.outbox.delete(operation.opId);
        return;
      }
      if (operation.entityType === "space") entitySnapshot = await this.#db.spaces.get(operation.entityId) ?? null;
      if (operation.entityType === "item") entitySnapshot = await this.#db.items.get(operation.entityId) ?? null;
      await this.#db.syncIssues.put({ id: `issue_${operation.opId}`, operation, code: result.errorCode ?? "rejected_operation", message: result.message ?? "The cloud rejected this operation", createdAt: Date.now(), entitySnapshot });
      await this.#db.outbox.delete(operation.opId);
    });
    this.#emit();
  }

  async search(query: string): Promise<SearchResult[]> {
    const [spaces, items] = await Promise.all([this.#db.spaces.toArray(), this.#db.items.toArray()]);
    return searchLocalContent(spaces, items, query);
  }

  async getCachedAsset(assetId: string): Promise<CachedAsset | undefined> {
    const asset = await this.#db.assets.get(assetId);
    if (asset) await this.#db.assets.update(assetId, { lastAccessedAt: Date.now() });
    return asset;
  }

  async putCachedAsset(asset: CachedAsset): Promise<void> {
    await this.#db.assets.put(asset);
  }

  async deleteCachedAsset(assetId: string): Promise<void> {
    await this.#db.assets.delete(assetId);
  }

  async evictCachedAssets(targetBytes: number): Promise<number> {
    const candidates = (await this.#db.assets.orderBy("lastAccessedAt").toArray()).filter((asset) => !asset.pinned);
    let removed = 0;
    for (const asset of candidates) {
      if (removed >= targetBytes) break;
      await this.#db.assets.delete(asset.assetId);
      removed += asset.byteLength;
    }
    return removed;
  }

  #emit(broadcast = true): void {
    for (const listener of this.#listeners) listener();
    if (broadcast) this.#channel?.postMessage("changed");
  }
}

function itemAssetIds(item: CanvasItem): string[] {
  if (item.props.kind === "image" || item.props.kind === "pdf" || item.props.kind === "spreadsheet") return [item.props.assetId];
  if (item.props.kind === "video") return [item.props.assetId, item.props.posterAssetId].filter((value): value is string => value !== null);
  if (item.props.kind === "web-clip") return item.props.previewAssetId ? [item.props.previewAssetId] : [];
  return [];
}
